/**
 * Errors on the rows of an editable or uploaded list, by field. Used by useRowEditor and
 * SpreadsheetUpload.
 */

// `row` holds errors that apply to the row as a whole, such as a duplicate key.
export type RowErrors<F extends string> = Partial<Record<F | 'row', string>>;

export const rowHasErrors = <F extends string>(errors: RowErrors<F> | undefined): boolean =>
  errors !== undefined && Object.values(errors).some(Boolean);

export const hasErrors = <F extends string>(rowErrors: RowErrors<F>[]): boolean =>
  rowErrors.some(rowHasErrors);

/** Indexes of the rows that have errors, in order. */
export const errorRowIndexes = <F extends string>(rowErrors: RowErrors<F>[]): number[] =>
  rowErrors.map((errors, index) => (rowHasErrors(errors) ? index : -1)).filter(index => index >= 0);

/**
 * Clears the row-level error, such as a duplicate key, on the rows whose `keyOf` is `key` once
 * fewer than two rows share it.
 */
export const clearResolvedDuplicates = <R, E extends { row?: string }>(
  rows: R[],
  rowErrors: E[],
  keyOf: (row: R) => string,
  key: string
): E[] => {
  if (rows.filter(row => keyOf(row) === key).length > 1) {
    return rowErrors;
  }
  return rowErrors.map((errors, i) =>
    keyOf(rows[i]) === key ? { ...errors, row: undefined } : errors
  );
};
